import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { createPortal } from "react-dom";
import { X, FileText, AlertTriangle, Mail, Phone, RefreshCw, ChevronRight, UserPlus, Rocket, CheckCircle2, Loader2, MessageSquareWarning } from "lucide-react";
import type { BienRow } from "@/schemas/bien";
import type { PrestataireRow } from "@/schemas/prestataire";
import { checkElsStates } from "@/v2/components/CheckElsBadges";
import { isElsLance, isPebLance, isPebDone } from "@/lib/bienPrepValidation";
import { getLifecycleStage } from "@/v2/lib/bienLifecycle";
import { cn } from "@/lib/utils";
import { FormField } from "@/components/ui/FormField";
import { TextAreaField } from "@/components/ui/TextAreaField";

/**
 * Popover d'actions du Pipeline ELS (proposition A validée 2026-06-03).
 *
 * Au clic sur une card, on n'ouvre PAS la fiche entière mais ce popover ancré,
 * qui ne montre que les actions UTILES au bien à son étape :
 *  - surfacing d'un refus prestataire (ELS / PEB) + raccourci pour réassigner ;
 *  - relancer le prestataire ELS / PEB (contact mailto/tel) ;
 *  - ouvrir la fiche complète.
 *
 * Tout est dérivé des données existantes (refus, checkElsStates, contacts).
 */

interface ProfileLite {
  id: string;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  telephone?: string | null;
}

interface Props {
  bien: BienRow;
  rect: DOMRect;
  prestataires: PrestataireRow[];
  /** Contact des personnes liées à `bien` uniquement (collaborateurSrs/
   *  expertPeb/prestataireEle) — depuis `useBienContacts(bien.id)` (migration
   *  126), jamais la table `profiles` complète. */
  profiles: ProfileLite[];
  /** Prestataires ELS assignables (actifs, spécialité état des lieux). */
  elsOptions: { value: string; label: string }[];
  /** Experts PEB assignables (actifs, spécialité expert-peb). */
  pebOptions: { value: string; label: string }[];
  /** (Ré)assigne le "Prestataire assigné" ELS depuis le popover. */
  onAssignEls: (prestataireId: string) => void;
  /** Assigne l'expert PEB depuis le popover (miroir ELS). */
  onAssignPeb: (expertId: string) => void;
  /** Id de l'option en cours d'assignation (ELS ou PEB) — retour visuel
   *  immédiat le temps de l'aller-retour réseau (signalé Thibaud 2026-08-19). */
  assigningId?: string | null;
  /** Lance manuellement une mission (ELS/PEB) → email + visibilité prestataire. */
  onLaunch: (mission: "els" | "peb") => void;
  /** Vrai pendant l'appel launch-mission POUR L'ELS uniquement — chaque
   *  bouton "Lancer" ne réagit qu'à sa propre mission (signalé Thibaud
   *  2026-08-19 : cliquer l'un ne doit pas affecter l'autre). */
  launchingEls?: boolean;
  /** Idem, pour le PEB. */
  launchingPeb?: boolean;
  /** Tranche "Appartement prêt pour photos" (true/false) sans ouvrir la fiche. */
  onSetPhotosReady: (value: boolean) => void;
  /** Met à jour indemnité/garantie (les 2 seuls champs qui conditionnent le
   *  bouton de clôture SRS, cf. BienDetailPage) + total/commentaire de
   *  clôture (informatifs, aucune garde) directement depuis le popover. */
  onUpdateSrsField: (patch: { indemnitePreavis?: string; garantieLiberee?: string; totalSrs?: string; commentaireSrs?: string }) => void;
  /** Clôture le dossier SRS côté collab (dossierCloture=true) — même geste
   *  que le bouton "Clôturer le dossier" de la fiche complète, activé sous
   *  la même garde (indemnité remplie + garantie="oui"). */
  onCloturerSrs: () => void;
  /** Validation finale C-Level/admin (dossierClos=true) — 2e étape distincte
   *  de dossierCloture, même geste que le 2e bouton de la fiche complète
   *  (cf. BienDetailPage : "Le C-Level / admin valide la fermeture"). */
  onValiderClotureSrs: () => void;
  /** Vrai pendant l'aller-retour réseau de n'importe laquelle des actions
   *  ci-dessus (même mutation générique côté page hôte). */
  savingSrs?: boolean;
  onOpenFiche: () => void;
  onClose: () => void;
}

type Person = { nom: string; email?: string; tel?: string; special?: boolean };

const POPOVER_W = 288;

export function PipelineActionsPopover({ bien, rect, prestataires, profiles, elsOptions, pebOptions, onAssignEls, onAssignPeb, assigningId, onLaunch, launchingEls, launchingPeb, onSetPhotosReady, onUpdateSrsField, onCloturerSrs, onValiderClotureSrs, savingSrs, onOpenFiche, onClose }: Props) {
  const { t } = useTranslation();
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const resolve = (id?: string | null): Person | null => {
    if (!id) return null;
    if (id === "amiable") return { nom: t("v2.pipeline.expert.amiable"), special: true };
    if (id === "autre") return { nom: t("v2.pipeline.expert.autre"), special: true };
    const p = prestataires.find((x) => x.id === id);
    if (p && (p.nom || "").trim()) return { nom: (p.nom || "").trim(), email: p.email ?? undefined, tel: p.telephone ?? undefined };
    const pr = profiles.find((x) => x.id === id);
    if (pr) {
      const nom = [pr.first_name, pr.last_name].filter(Boolean).join(" ").trim();
      if (nom) return { nom, email: pr.email ?? undefined, tel: pr.telephone ?? undefined };
    }
    return { nom: "—" };
  };

  const s = checkElsStates(bien);
  // Assignation ELS active = prestataireEle ("Prestataire assigné"). prestataireEls
  // = trace pure (modèle 2026-06-06) → ne sert pas à l'affichage/contact.
  const els = resolve(bien.prestataireEle);
  const peb = resolve(bien.expertPeb);
  const elsRefused = !!bien.prestataireEleRefuseAt;
  const pebRefused = !!bien.expertPebRefuseAt;
  const elsAssignedReal = !!bien.prestataireEle && bien.prestataireEle !== "amiable" && bien.prestataireEle !== "autre";
  const pebAssignedReal = !!bien.expertPeb && bien.expertPeb !== "amiable" && bien.expertPeb !== "autre";
  // Un process n'appelle une action que s'il est DEMANDÉ dans ce cycle et pas
  // encore fait. Côté PEB, « fait » = `statutPeb` (`isPebDone`), plus la
  // validité du certificat en vigueur : un PEB demandé explicitement sur un
  // certificat encore valide n'avait ni « Définir » ni « Lancer » (HK_H4.4,
  // 2026-09-15) ; et un cycle PEB seul proposait à tort de définir un
  // prestataire ELS.
  const bd = bien as unknown as Record<string, unknown>;
  const needsEls = isElsLance(bd) && s.els !== "success" && !bien.dossierClos;
  const needsPeb = isPebLance(bd) && !isPebDone(bd) && !bien.dossierClos;
  // Lancement manuel (mig 090) : la mission ne part (email + visibilité prestataire)
  // qu'au clic « Lancer ». Tant que le prestataire est assigné mais pas lancé, on
  // propose le bouton ; une fois lancé, on montre un état passif + la relance.
  const elsLaunched = !!bien.elsMissionLanceeAt;
  const pebLaunched = !!bien.pebMissionLanceeAt;
  // Pas de prestataire ELS assigné (et l'ELS est encore à faire) → proposer d'en définir un.
  const showDefineEls = needsEls && !elsRefused && !elsAssignedReal;
  // Miroir côté PEB — absent jusqu'ici (signalé Thibaud 2026-08-19 : "pas uniforme").
  const showDefinePeb = needsPeb && !pebRefused && !pebAssignedReal;
  const showLaunchEls = needsEls && !elsRefused && elsAssignedReal && !elsLaunched;
  const showLaunchPeb = needsPeb && !pebRefused && pebAssignedReal && !pebLaunched;
  // "Prestataire précédent" (prestataireEls, trace pure) : suggestion visuelle
  // dans la liste ELS — l'info existait déjà sur la fiche complète mais pas ici
  // (signalé Thibaud 2026-08-19). Pas d'équivalent PEB : pas de notion de
  // "précédent" pour un expert PEB (cf. schema).
  const suggestedElsId = bien.prestataireEls || undefined;
  // La relance (contact du prestataire) n'a de sens qu'après lancement.
  const elsRelance = !!els && !els.special && needsEls && !elsRefused && elsLaunched;
  const pebRelance = !!peb && !peb.special && needsPeb && !pebRefused && pebLaunched;
  const nothing = !elsRefused && !pebRefused && !elsRelance && !pebRelance && !showDefineEls && !showDefinePeb && !showLaunchEls && !showLaunchPeb;
  // Dès qu'il n'y a plus d'action ELS/PEB à proposer ici, le popover disait
  // "Aucune action en attente" — trompeur : il peut rester le dossier SRS à
  // clôturer et/ou la décision "prêt pour photos" à trancher, deux actions
  // qui vivent sur la fiche complète, hors scope de ce popover (signalé
  // Thibaud 2026-08-20 : "c'est faux... il faut clôturer le SRS" puis
  // "l'autre action à faire : prêt pour photo"). `s.srs`/`s.picts` sont les
  // MÊMES badges que ceux affichés sous la card (CheckElsBadges) — aucune
  // logique dupliquée.
  const pendingSrs = nothing && s.srs !== "success";
  // "Prêt pour photos" est une décision du collab pendant la finalisation SRS
  // (stage els_done, colonne "À CLÔTURER (COLLAB)") — pas du C-Level qui ne
  // fait que valider la clôture finale (stage closing, colonne "SRS À
  // CLÔTURER"). Restreint à els_done pour ne plus lui être proposée là où ce
  // n'est pas sa décision (signalé Thibaud 2026-08-20 : "il doit même pas
  // voir cela").
  const stage = getLifecycleStage(bien).stage;
  const pendingPhotos = nothing && s.picts !== "success" && stage === "els_done";
  // Validation finale (dossierClos) : dès que le collab a clôturé
  // (dossierCloture=true → s.srs déjà "success" → pendingSrs ci-dessus
  // devient faux), le popover ne proposait plus RIEN au C-Level dans cette
  // colonne — obligé d'ouvrir la fiche complète juste pour lire les 4 champs
  // du collab et cliquer le 2e bouton (signalé Thibaud 2026-08-20 : "je ne
  // peux que clôturer le dossier srs et consulter les infos... évitons de
  // devoir rentrer dans la fiche"). Stage "closing" = dossierCloture=true
  // par construction (cf. operationalElsStage) ; ne reste qu'à checker
  // dossierClos pour ne plus la proposer une fois réellement fermé.
  const pendingValidation = nothing && stage === "closing" && !bien.dossierClos;
  const otherPending = pendingSrs || pendingPhotos || pendingValidation;
  const indemniteLabelMap: Record<string, string> = {
    non: t("biens.indemnitePreavisOptions.non"),
    "1": t("biens.indemnitePreavisOptions.un"),
    "2": t("biens.indemnitePreavisOptions.deux"),
    "3": t("biens.indemnitePreavisOptions.trois"),
  };

  const left = Math.max(8, Math.min(rect.right + 8, window.innerWidth - POPOVER_W - 8));
  const top = Math.min(Math.max(rect.top, 8), Math.max(8, window.innerHeight - 380));

  return createPortal(
    <div className="fixed inset-0 z-50" onClick={onClose}>
      <div
        className="absolute bg-surface border border-border rounded-xl shadow-2xl w-72 overflow-hidden"
        style={{ left, top }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
      >
        <div className="px-3 py-2.5 border-b border-border flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="text-sm font-mono font-semibold text-text-primary truncate">
              {bien.referenceInterne || bien.reference || "—"}
            </div>
            <div className="text-[11px] text-text-muted truncate">
              {bien.adresse || "—"}{bien.ville ? ` · ${bien.ville}` : ""}
            </div>
          </div>
          <button type="button" onClick={onClose} className="text-text-muted hover:text-text-primary cursor-pointer flex-shrink-0">
            <X size={15} />
          </button>
        </div>

        <div className="p-2 flex flex-col gap-1.5 max-h-[60vh] overflow-y-auto">
          {/* Les 2 commentaires prestataire (ELS + PEB, pas commentaireSrs qui
              est celui du collab) — signalé Thibaud 2026-08-20 : "il faudrait
              tout les commentaires des prestataires et savoir de qui c'est".
              Attribution = même résolution `els`/`peb` que le reste du
              popover (prestataireEle/expertPeb), pas de logique dupliquée. */}
          {!!bien.edlCommentaire?.trim() && (
            <PrestataireCommentBlock
              title={els && !els.special && els.nom !== "—" ? `${t("v2.pipeline.actions.prestaCommentElsTitle")} — ${els.nom}` : t("v2.pipeline.actions.prestaCommentElsTitle")}
              text={bien.edlCommentaire.trim()}
            />
          )}
          {!!bien.pebCommentaire?.trim() && (
            <PrestataireCommentBlock
              title={peb && !peb.special && peb.nom !== "—" ? `${t("v2.pipeline.actions.prestaCommentPebTitle")} — ${peb.nom}` : t("v2.pipeline.actions.prestaCommentPebTitle")}
              text={bien.pebCommentaire.trim()}
            />
          )}
          {elsRefused && (
            <AssignBlock
              refused
              title={t("v2.pipeline.elsRefuse")}
              detail={`${t("v2.pipeline.actions.elsRefuseDetail")}${(() => { const w = resolve(bien.prestataireEleRefusePar)?.nom; return w && w !== "—" ? ` (${w})` : ""; })()}`}
              pickLabel={t("v2.pipeline.actions.chooseEls")}
              options={elsOptions}
              onPick={onAssignEls}
              emptyLabel={t("v2.pipeline.actions.noElsPresta")}
              suggestedId={suggestedElsId}
              suggestedLabel={t("v2.pipeline.actions.suggested")}
              assigningId={assigningId}
            />
          )}
          {showDefineEls && (
            <AssignBlock
              title={t("v2.pipeline.actions.defineEls")}
              pickLabel={t("v2.pipeline.actions.chooseEls")}
              options={elsOptions}
              onPick={onAssignEls}
              emptyLabel={t("v2.pipeline.actions.noElsPresta")}
              suggestedId={suggestedElsId}
              suggestedLabel={t("v2.pipeline.actions.suggested")}
              assigningId={assigningId}
            />
          )}
          {pebRefused && (
            <RefusBlock
              label={t("v2.pipeline.pebRefuse")}
              detail={t("v2.pipeline.actions.pebRefuseDetail")}
              who={resolve(bien.expertPebRefusePar)?.nom}
              changeLabel={t("v2.pipeline.actions.changePeb")}
              onChange={onOpenFiche}
            />
          )}
          {showDefinePeb && (
            <AssignBlock
              title={t("v2.pipeline.actions.definePeb")}
              pickLabel={t("v2.pipeline.actions.choosePeb")}
              options={pebOptions}
              onPick={onAssignPeb}
              emptyLabel={t("v2.pipeline.actions.noPebExpert")}
              assigningId={assigningId}
            />
          )}

          {showLaunchEls && (
            <LaunchBlock
              title={t("v2.pipeline.actions.launchEls")}
              detail={els && els.nom !== "—" ? `${t("v2.pipeline.actions.launchDetail")} ${els.nom}` : t("v2.pipeline.actions.launchDetailNoName")}
              cta={t("v2.pipeline.actions.launchCta")}
              disabled={!!launchingEls}
              loading={!!launchingEls}
              onLaunch={() => onLaunch("els")}
            />
          )}
          {showLaunchPeb && (
            <LaunchBlock
              title={t("v2.pipeline.actions.launchPeb")}
              detail={peb && peb.nom !== "—" ? `${t("v2.pipeline.actions.launchDetail")} ${peb.nom}` : t("v2.pipeline.actions.launchDetailNoName")}
              cta={t("v2.pipeline.actions.launchCta")}
              disabled={!!launchingPeb}
              loading={!!launchingPeb}
              onLaunch={() => onLaunch("peb")}
            />
          )}
          {elsLaunched && s.els !== "success" && !elsRefused && <LaunchedTag label={t("v2.pipeline.actions.elsLaunched")} at={bien.elsMissionLanceeAt} />}
          {pebLaunched && needsPeb && !pebRefused && <LaunchedTag label={t("v2.pipeline.actions.pebLaunched")} at={bien.pebMissionLanceeAt} />}

          {elsRelance && <RelanceRow title={t("v2.pipeline.actions.relanceEls")} person={els!} emptyContact={t("v2.pipeline.actions.noContact")} />}
          {pebRelance && <RelanceRow title={t("v2.pipeline.actions.relancePeb")} person={peb!} emptyContact={t("v2.pipeline.actions.noContact")} />}

          {pendingPhotos && (
            <ChoiceBlock
              title={t("v2.pipeline.actions.pendingPhotos")}
              options={[
                { value: "true", label: t("common.yes") },
                { value: "false", label: t("common.no") },
              ]}
              onPick={(v) => onSetPhotosReady(v === "true")}
              disabled={!!savingSrs}
            />
          )}
          {pendingSrs && (
            <SrsClotureBlock
              key={bien.id}
              indemnite={bien.indemnitePreavis || ""}
              garantie={bien.garantieLiberee || ""}
              total={bien.totalSrs || ""}
              commentaire={bien.commentaireSrs || ""}
              // Même garde que la fiche complète (signalé Thibaud 2026-08-20 :
              // "je ne peux pas clôturer tant que le transfert élec n'est pas
              // fait... la fiche entière laisse bypasser" — corrigé aux 2
              // endroits). Ignoré si le bien ne nécessite pas d'ELS.
              elecBlocked={bien.elsRequis !== false && s.elec !== "success"}
              elecBlockedLabel={t("biens.clotureBloquee.elec")}
              onUpdate={onUpdateSrsField}
              onCloturer={onCloturerSrs}
              saving={!!savingSrs}
              title={t("v2.pipeline.actions.pendingSrs")}
              indemniteLabel={t("biens.indemnitePreavisQ")}
              indemniteOptions={[
                { value: "non", label: t("biens.indemnitePreavisOptions.non") },
                { value: "1", label: t("biens.indemnitePreavisOptions.un") },
                { value: "2", label: t("biens.indemnitePreavisOptions.deux") },
                { value: "3", label: t("biens.indemnitePreavisOptions.trois") },
              ]}
              garantieLabel={t("biens.garantieLiberee")}
              garantieOptions={[
                { value: "oui", label: t("common.yes") },
                { value: "non", label: t("common.no") },
              ]}
              totalLabel={t("biens.totalSrs")}
              commentaireLabel={t("biens.commentaireSrsLabel")}
              cta={t("biens.cloturerDossier")}
            />
          )}
          {pendingValidation && (
            <SrsValidationBlock
              title={t("v2.pipeline.actions.pendingValidation")}
              indemniteLabel={t("biens.indemnitePreavisQ")}
              indemniteText={indemniteLabelMap[bien.indemnitePreavis || ""] || "—"}
              garantieLabel={t("biens.garantieLiberee")}
              garantieText={bien.garantieLiberee === "oui" ? t("common.yes") : bien.garantieLiberee === "non" ? t("common.no") : "—"}
              totalLabel={t("biens.totalSrs")}
              totalText={bien.totalSrs || "—"}
              commentaireLabel={t("biens.commentaireSrsLabel")}
              commentaireText={bien.commentaireSrs || ""}
              cta={t("biens.dossierCloture")}
              saving={!!savingSrs}
              onValider={onValiderClotureSrs}
            />
          )}
          {nothing && !otherPending && (
            <div className="text-[11px] text-text-muted px-1.5 py-1.5">{t("v2.pipeline.actions.nothing")}</div>
          )}

          <button
            type="button"
            onClick={onOpenFiche}
            className="mt-1 flex items-center justify-between gap-2 px-2 py-2 rounded-md text-sm text-text-secondary hover:bg-surface-2 cursor-pointer border-t border-border/50"
          >
            <span className="flex items-center gap-2"><FileText size={14} /> {t("v2.pipeline.actions.openFiche")}</span>
            <ChevronRight size={14} className="text-text-muted" />
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function AssignBlock({ refused, title, detail, pickLabel, options, onPick, emptyLabel, suggestedId, suggestedLabel, assigningId }: {
  refused?: boolean; title: string; detail?: string; pickLabel: string;
  options: { value: string; label: string }[]; onPick: (id: string) => void; emptyLabel: string;
  /** Id de l'option à repérer comme suggestion (ex : prestataire de l'EDL
   *  précédent) — info déjà présente sur la fiche complète mais invisible ici
   *  jusque-là (signalé Thibaud 2026-08-19). */
  suggestedId?: string;
  suggestedLabel?: string;
  /** Id de l'option en cours d'assignation — spinner dessus, tout le reste
   *  désactivé le temps de l'aller-retour (signalé Thibaud 2026-08-19 : sans
   *  ça, rien ne dit que le clic a été pris en compte). */
  assigningId?: string | null;
}) {
  const busy = !!assigningId;
  return (
    <div className={refused ? "rounded-md border border-danger/30 bg-danger/5 p-2" : "rounded-md border border-border p-2"}>
      <div className={`flex items-center gap-1.5 text-xs font-bold ${refused ? "text-danger" : "text-text-primary"}`}>
        {refused && <AlertTriangle size={13} />} {title}
      </div>
      {detail && <div className="text-[11px] text-text-muted mt-0.5">{detail}</div>}
      <div className="text-[11px] text-text-muted mt-1.5 mb-1">{pickLabel}</div>
      <div className="flex flex-col gap-1">
        {options.length === 0 ? (
          <span className="text-[11px] text-text-muted/70">{emptyLabel}</span>
        ) : (
          options.map((o) => {
            const suggested = !!suggestedId && o.value === suggestedId;
            const assigningThis = assigningId === o.value;
            return (
              <button
                key={o.value}
                type="button"
                onClick={() => onPick(o.value)}
                disabled={busy}
                className={
                  (suggested
                    ? "w-full flex items-center justify-between gap-1.5 px-2 py-1.5 rounded-md text-xs text-text-primary bg-accent/5 border border-accent text-left"
                    : "w-full flex items-center gap-1.5 px-2 py-1.5 rounded-md text-xs text-text-primary bg-surface border border-border hover:border-accent hover:bg-accent/5 text-left") +
                  (busy ? " cursor-wait disabled:opacity-50" : " cursor-pointer")
                }
              >
                <span className="flex items-center gap-1.5 min-w-0">
                  {assigningThis ? (
                    <Loader2 size={12} className="text-accent flex-shrink-0 animate-spin" />
                  ) : (
                    <UserPlus size={12} className="text-accent flex-shrink-0" />
                  )}
                  <span className="truncate">{o.label}</span>
                </span>
                {suggested && !assigningThis && (
                  <span className="flex-shrink-0 text-[9.5px] font-semibold uppercase tracking-wide text-accent">{suggestedLabel}</span>
                )}
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}

function LaunchBlock({ title, detail, cta, disabled, loading, onLaunch }: {
  title: string; detail: string; cta: string; disabled: boolean; loading?: boolean; onLaunch: () => void;
}) {
  return (
    <div className="rounded-md border border-accent/30 bg-accent/5 p-2">
      <div className="flex items-center gap-1.5 text-xs font-bold text-text-primary">
        <Rocket size={13} className="text-accent" /> {title}
      </div>
      <div className="text-[11px] text-text-muted mt-0.5">{detail}</div>
      <button
        type="button"
        onClick={onLaunch}
        disabled={disabled}
        className="mt-1.5 w-full flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-md text-xs font-semibold bg-accent text-white hover:bg-accent-hover cursor-pointer disabled:opacity-50 disabled:cursor-wait"
      >
        {loading ? <Loader2 size={12} className="animate-spin" /> : <Rocket size={12} />} {cta}
      </button>
    </div>
  );
}

function LaunchedTag({ label, at }: { label: string; at?: string | null }) {
  const d = at ? new Date(at) : null;
  const fmt = d && !isNaN(d.getTime())
    ? `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`
    : "";
  return (
    <div className="flex items-center gap-1.5 px-1.5 py-1 text-[11px] font-medium text-success">
      <CheckCircle2 size={12} /> {label}{fmt ? ` — ${fmt}` : ""}
    </div>
  );
}

function RefusBlock({ label, detail, who, changeLabel, onChange }: {
  label: string; detail: string; who?: string; changeLabel: string; onChange: () => void;
}) {
  return (
    <div className="rounded-md border border-danger/30 bg-danger/5 p-2">
      <div className="flex items-center gap-1.5 text-danger text-xs font-bold">
        <AlertTriangle size={13} /> {label}
      </div>
      <div className="text-[11px] text-text-muted mt-0.5">
        {detail}{who && who !== "—" ? ` (${who})` : ""}
      </div>
      <button
        type="button"
        onClick={onChange}
        className="mt-1.5 w-full flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-md text-xs font-semibold bg-accent text-white hover:bg-accent-hover cursor-pointer"
      >
        <RefreshCw size={12} /> {changeLabel}
      </button>
    </div>
  );
}

/**
 * Choix rapide à 2 options (ex. "prêt pour photos" oui/non) — boutons plats,
 * PAS un `SelectField` : son panneau déroulant est en `position: absolute`,
 * qui se ferait couper par le conteneur scrollable de ce popover (même bug
 * que DateField/TimeField corrigé le 2026-08-20). Aucun risque avec des
 * boutons simples.
 */
function ChoiceBlock({ title, options, onPick, disabled }: {
  title: string; options: { value: string; label: string }[]; onPick: (value: string) => void; disabled?: boolean;
}) {
  return (
    <div className="rounded-md border border-warning/30 bg-warning/5 p-2">
      <div className="flex items-center gap-1.5 text-xs font-bold text-warning">
        <AlertTriangle size={13} /> {title}
      </div>
      <div className="flex gap-1.5 mt-1.5">
        {options.map((o) => (
          <button
            key={o.value}
            type="button"
            disabled={disabled}
            onClick={() => onPick(o.value)}
            className="flex-1 px-2 py-1.5 rounded-md text-xs font-semibold bg-surface border border-border hover:border-accent hover:bg-accent/5 text-text-primary cursor-pointer disabled:opacity-50 disabled:cursor-wait"
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * Clôture du dossier SRS directement depuis le popover — mêmes 2 champs et la
 * même garde que le bouton "Clôturer le dossier" de la fiche complète
 * (indemnité remplie + garantie = "oui", cf. BienDetailPage) : demandé par
 * Thibaud la veille d'une démo pour éviter d'ouvrir la fiche entière juste
 * pour ces 2 réponses. Boutons plats plutôt que SelectField (même raison que
 * ChoiceBlock ci-dessus).
 */
function SrsClotureBlock({ indemnite, garantie, total, commentaire, elecBlocked, elecBlockedLabel, onUpdate, onCloturer, saving, title, indemniteLabel, indemniteOptions, garantieLabel, garantieOptions, totalLabel, commentaireLabel, cta }: {
  indemnite: string; garantie: string; total: string; commentaire: string;
  elecBlocked: boolean; elecBlockedLabel: string;
  onUpdate: (patch: { indemnitePreavis?: string; garantieLiberee?: string; totalSrs?: string; commentaireSrs?: string }) => void;
  onCloturer: () => void;
  saving: boolean;
  title: string; indemniteLabel: string; indemniteOptions: { value: string; label: string }[];
  garantieLabel: string; garantieOptions: { value: string; label: string }[];
  totalLabel: string; commentaireLabel: string; cta: string;
}) {
  const canCloture = !!indemnite && garantie === "oui" && !elecBlocked && !saving;
  // FormField/TextAreaField gèrent leur propre `onBlur` interne (état focus
  // visuel) — un `onBlur` passé en prop écraserait ce handler via le spread
  // `{...props}`. On sauvegarde donc au blur du conteneur (bulle depuis
  // l'input natif) plutôt qu'en prop direct, pour ne pas toucher aux
  // composants partagés. État local le temps de la frappe pour ne pas
  // déclencher une mutation réseau à chaque caractère — remis à zéro en
  // changeant de bien via `key={bien.id}` au call site (pas de useEffect de
  // resynchronisation : un refetch en cours de frappe n'écrase jamais ce que
  // l'utilisateur est en train de taper).
  const [totalLocal, setTotalLocal] = useState(total);
  const [commentaireLocal, setCommentaireLocal] = useState(commentaire);
  return (
    <div className="rounded-md border border-warning/30 bg-warning/5 p-2 flex flex-col gap-2">
      <div className="flex items-center gap-1.5 text-xs font-bold text-warning">
        <AlertTriangle size={13} /> {title}
      </div>
      <div>
        <div className="text-[11px] text-text-muted mb-1">{indemniteLabel}</div>
        <div className="grid grid-cols-2 gap-1">
          {indemniteOptions.map((o) => (
            <button
              key={o.value}
              type="button"
              disabled={saving}
              onClick={() => onUpdate({ indemnitePreavis: o.value })}
              className={cn(
                "px-2 py-1 rounded-md text-[11px] font-medium cursor-pointer disabled:opacity-50 disabled:cursor-wait truncate",
                indemnite === o.value
                  ? "bg-accent text-accent-foreground"
                  : "bg-surface border border-border text-text-primary hover:border-accent hover:bg-accent/5",
              )}
            >
              {o.label}
            </button>
          ))}
        </div>
      </div>
      <div>
        <div className="text-[11px] text-text-muted mb-1">{garantieLabel}</div>
        <div className="flex gap-1">
          {garantieOptions.map((o) => (
            <button
              key={o.value}
              type="button"
              disabled={saving}
              onClick={() => onUpdate({ garantieLiberee: o.value })}
              className={cn(
                "flex-1 px-2 py-1 rounded-md text-[11px] font-medium cursor-pointer disabled:opacity-50 disabled:cursor-wait",
                garantie === o.value
                  ? "bg-accent text-accent-foreground"
                  : "bg-surface border border-border text-text-primary hover:border-accent hover:bg-accent/5",
              )}
            >
              {o.label}
            </button>
          ))}
        </div>
      </div>
      <div onBlur={() => { if (totalLocal !== total) onUpdate({ totalSrs: totalLocal }); }}>
        <FormField label={totalLabel} value={totalLocal} onChange={setTotalLocal} disabled={saving} />
      </div>
      <div onBlur={() => { if (commentaireLocal !== commentaire) onUpdate({ commentaireSrs: commentaireLocal }); }}>
        <TextAreaField label={commentaireLabel} value={commentaireLocal} onChange={setCommentaireLocal} disabled={saving} rows={2} />
      </div>
      {/* Uniquement affichée quand c'est réellement ce qui bloque — pas un
          rappel permanent (demande explicite Thibaud : "ne mettre l'alerte
          que dans ce cas"). */}
      {elecBlocked && (
        <p className="flex items-start gap-1.5 text-[11px] text-text-muted">
          <AlertTriangle size={12} className="shrink-0 mt-0.5 text-warning" />
          <span>{elecBlockedLabel}</span>
        </p>
      )}
      <button
        type="button"
        disabled={!canCloture}
        onClick={onCloturer}
        className="w-full flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-md text-xs font-semibold bg-accent text-white hover:bg-accent-hover cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
      >
        {saving ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />} {cta}
      </button>
    </div>
  );
}

/**
 * Étape 2 (C-Level/admin) : recap en lecture seule des 4 champs remplis par
 * le collab à l'étape 1 (mêmes valeurs que SrsClotureBlock, non éditables
 * ici — les modifier reste le rôle du collab) + bouton de validation finale
 * (dossierClos). Sans ce bloc, le C-Level n'avait aucun moyen de consulter
 * ces champs ni de clôturer sans ouvrir la fiche complète (signalé Thibaud
 * 2026-08-20 : "je ne peux que clôturer le dossier srs et consulter les
 * infos des champs remplis par le collaborateur... évitons de devoir rentrer
 * dans la fiche").
 */
function SrsValidationBlock({ title, indemniteLabel, indemniteText, garantieLabel, garantieText, totalLabel, totalText, commentaireLabel, commentaireText, cta, saving, onValider }: {
  title: string;
  indemniteLabel: string; indemniteText: string;
  garantieLabel: string; garantieText: string;
  totalLabel: string; totalText: string;
  commentaireLabel: string; commentaireText: string;
  cta: string; saving: boolean; onValider: () => void;
}) {
  return (
    <div className="rounded-md border border-success/30 bg-success/5 p-2 flex flex-col gap-2">
      <div className="flex items-center gap-1.5 text-xs font-bold text-success">
        <CheckCircle2 size={13} /> {title}
      </div>
      <div className="flex flex-col gap-1">
        <RecapRow label={indemniteLabel} value={indemniteText} />
        <RecapRow label={garantieLabel} value={garantieText} />
        <RecapRow label={totalLabel} value={totalText} />
        {commentaireText && (
          <div className="text-[11px] mt-0.5">
            <div className="text-text-muted">{commentaireLabel}</div>
            <div className="text-text-primary whitespace-pre-wrap mt-0.5">{commentaireText}</div>
          </div>
        )}
      </div>
      {/* Vert (pas bg-accent) : même bouton que sur la fiche complète, même
          logique — action ENCORE À FAIRE par le C-Level, pas un état passif
          (signalé Thibaud 2026-08-20). */}
      <button
        type="button"
        disabled={saving}
        onClick={onValider}
        className="w-full flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-md text-xs font-semibold bg-success text-white hover:bg-success/90 cursor-pointer disabled:opacity-50 disabled:cursor-wait"
      >
        {saving ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />} {cta}
      </button>
    </div>
  );
}

function RecapRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-2 text-[11px]">
      <span className="text-text-muted flex-shrink-0">{label}</span>
      <span className="text-text-primary font-medium text-right truncate">{value}</span>
    </div>
  );
}

/** Commentaire laissé par le prestataire lors de l'EDL — lecture seule,
 *  affiché en haut du popover pour ne plus dépendre du tooltip natif du
 *  badge "Rem ELS" de la card, masqué par le popover une fois ouvert
 *  (signalé Thibaud 2026-08-20 : "consulter le commentaire du prestataire"). */
function PrestataireCommentBlock({ title, text }: { title: string; text: string }) {
  return (
    <div className="rounded-md border border-border bg-surface-2/50 p-2">
      <div className="flex items-center gap-1.5 text-xs font-bold text-text-primary">
        <MessageSquareWarning size={13} className="text-danger" /> {title}
      </div>
      <div className="text-[11px] text-text-secondary mt-1 whitespace-pre-wrap">{text}</div>
    </div>
  );
}

function RelanceRow({ title, person, emptyContact }: { title: string; person: Person; emptyContact: string }) {
  return (
    <div className="rounded-md border border-border p-2">
      <div className="text-xs font-semibold text-text-primary">{title}</div>
      <div className="text-[11px] text-text-muted">{person.nom}</div>
      <div className="flex flex-col gap-0.5 mt-1">
        {person.email && (
          <a href={`mailto:${person.email}`} className="flex items-center gap-1.5 text-[11px] text-accent font-medium hover:underline">
            <Mail size={11} /> {person.email}
          </a>
        )}
        {person.tel && (
          <a href={`tel:${person.tel.replace(/\s/g, "")}`} className="flex items-center gap-1.5 text-[11px] text-accent font-medium hover:underline">
            <Phone size={11} /> {person.tel}
          </a>
        )}
        {!person.email && !person.tel && (
          <span className="text-[11px] text-text-muted/70">{emptyContact}</span>
        )}
      </div>
    </div>
  );
}
